import { Entity, EntityMap, moveToFulfilled, update } from '@ms/yammer-data-support';
import { prependTitleToRawContentState } from '@ms/yammer-draft';
import { LimitedReaction, MessageDeletionOutcome, limitedReactions } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/actions.types';
import { DeleteFileRejectedAction } from '../../domains/deleteFile/actions.types';
import { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import {
  AddUpvotePending,
  AddUpvoteRejected,
  AnswerAddUpvoteActions,
  RemoveUpvoteActions,
  RemoveUpvotePending,
  RemoveUpvoteRejected,
} from '../../domains/messageAddRemoveUpvote/actions.types';
import {
  MessageLikeAction,
  MessageLikePendingAction,
  MessageLikeRejectedAction,
  MessageRealtimeReaction,
  MessageUnlikeAction,
  MessageUnlikePendingAction,
  ResetFailedFileDeletionAction,
} from '../../domains/messages/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import { MarkAllNotificationsAsSeenFulfilledAction } from '../../domains/notificationFeeds/actions.types';
import { NotificationLoadMoreFulfilledAction } from '../../domains/notificationPaginations/actions.types';
import {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import { PollLoadFulfilledAction, PollVotePendingAction } from '../../domains/polls/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import { ThreadListLoadFulfilledAction } from '../../domains/threadListLoad/actions.types';
import {
  ThreadLoadFulfilledAction,
  ThreadLoadNewerRepliesFulfilledAction,
  ThreadLoadOlderRepliesFulfilledAction,
} from '../../domains/threadLoad/actions.types';
import {
  ThreadMarkAsQuestionFulfilledAction,
  ThreadUnmarkAsQuestionFulfilledAction,
} from '../../domains/threadMarkAsQuestion/types';
import { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import { User } from '../users/types';

import { Message, MessageSubtype, Messages, NormalMessageContent, isPollMessageContent } from './types';
export type MessageAction =
  | DeleteFileRejectedAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | ThreadLoadFulfilledAction
  | ThreadLoadNewerRepliesFulfilledAction
  | ThreadLoadOlderRepliesFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | ThreadListLoadFulfilledAction
  | MessageLikeAction
  | MessageUnlikeAction
  | MessageRealtimeReaction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | ThreadMarkAsQuestionFulfilledAction
  | ThreadUnmarkAsQuestionFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | PollVotePendingAction
  | PollLoadFulfilledAction
  | DeleteMessageFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | MarkAllNotificationsAsSeenFulfilledAction
  | NotificationLoadMoreFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | AnswerAddUpvoteActions
  | RemoveUpvoteActions
  | ResetFailedFileDeletionAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | CuratedThreadListLoadAction;

const initialState: Messages = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const maxFeaturedIds = 8;

const stateWithUpdatedReactions = (
  state: EntityMap<Message>,
  id: Message['id'],
  updatedReactionState: Partial<Message>
) => update(state, id, updatedReactionState);

const addReaction = (message: Message, reaction: LimitedReaction): Message => ({
  ...message,
  reactionsCounts: {
    ...message.reactionsCounts,
    [reaction]: message.reactionsCounts[reaction] + 1,
  },
});

const removeReaction = (message: Message, reaction: LimitedReaction): Message => ({
  ...message,
  reactionsCounts: {
    ...message.reactionsCounts,
    [reaction]: message.reactionsCounts[reaction] - 1,
  },
});

const removeFeaturedReactionUserId = (message: Message, userId: User['id'], reaction: LimitedReaction): Message => ({
  ...message,
  featuredUserIdsByReaction: {
    ...message.featuredUserIdsByReaction,
    [reaction]: message.featuredUserIdsByReaction[reaction].filter((id) => id !== userId),
  },
});

const addUserLikeToMessage = (state: Messages, action: MessageLikePendingAction) => {
  const { id, reaction } = action.payload;
  const message = state.fulfilled.byId[id];
  const { reactionsTotalCount, viewerReaction } = message;

  if (reaction !== viewerReaction) {
    const unlikedMessage = viewerReaction != null ? removeReaction(message, viewerReaction) : message;
    const changedReactionMessage = addReaction(unlikedMessage, reaction);
    const updatedReactionState = {
      ...changedReactionMessage,
      reactionsTotalCount: reactionsTotalCount + (viewerReaction != null ? 0 : 1),
      viewerHasLiked: true,
      viewerReaction: reaction,
    };

    return stateWithUpdatedReactions(state, id, updatedReactionState);
  }

  return stateWithUpdatedReactions(state, id, {});
};

const removeUserLikeFromMessage = (state: Messages, action: MessageLikeRejectedAction | MessageUnlikePendingAction) => {
  const { id } = action.payload;
  const message = state.fulfilled.byId[id];
  const { reactionsTotalCount, viewerReaction } = message;

  if (viewerReaction != null) {
    const unlikedMessage = removeReaction(message, viewerReaction);
    const updatedReactionState = {
      ...unlikedMessage,
      reactionsTotalCount: reactionsTotalCount - 1,
      viewerHasLiked: false,
      viewerReaction: undefined,
    };

    return stateWithUpdatedReactions(state, id, updatedReactionState);
  }

  return stateWithUpdatedReactions(state, id, {});
};

const removeFeaturedReaction = (message: Message, userId: User['id'], reaction: LimitedReaction): Message => {
  const { reactionsTotalCount, featuredReactionUserIds } = message;
  let updatedState = removeFeaturedReactionUserId(message, userId, reaction);
  updatedState = removeReaction(updatedState, reaction);

  const updatedMessage = {
    ...updatedState,
    featuredReactionUserIds: featuredReactionUserIds.filter((id) => id !== userId),
    reactionsTotalCount: reactionsTotalCount - 1,
  };

  return updatedMessage;
};

const removeFeaturedUserIdWithoutReaction = (message: Message, userId: User['id']): Message => {
  const { featuredUserIdsByReaction } = message;
  let updatedMessage = message;
  for (const reaction of limitedReactions) {
    if (featuredUserIdsByReaction[reaction].includes(userId)) {
      updatedMessage = removeFeaturedReaction(message, userId, reaction as LimitedReaction);
    }
  }

  return updatedMessage;
};

const addFeaturedReaction = (message: Message, reaction: LimitedReaction, userId: User['id']): Message => {
  const { reactionsTotalCount, featuredReactionUserIds, featuredUserIdsByReaction } = message;
  const updatedFeaturedReactionUserIds =
    featuredReactionUserIds.length < maxFeaturedIds ? [...featuredReactionUserIds, userId] : featuredReactionUserIds;
  const updatedFeaturedUserIdsByReaction =
    featuredReactionUserIds.length < maxFeaturedIds
      ? [...featuredUserIdsByReaction[reaction], userId]
      : featuredUserIdsByReaction[reaction];

  const updatedMessage = {
    ...message,
    featuredReactionUserIds: updatedFeaturedReactionUserIds,
    featuredUserIdsByReaction: {
      ...message.featuredUserIdsByReaction,
      [reaction]: updatedFeaturedUserIdsByReaction,
    },
    reactionsTotalCount: reactionsTotalCount + 1,
  };

  return addReaction(updatedMessage, reaction);
};

const addRealtimeReaction = (state: Messages, action: MessageRealtimeReaction) => {
  const { id, reaction, userId } = action.payload;
  const message = state.fulfilled.byId[id];

  if (!message) {
    return state;
  }
  const updatedReactionState = removeFeaturedUserIdWithoutReaction(message, userId);
  const finalReactionState = addFeaturedReaction(updatedReactionState, reaction, userId);

  return stateWithUpdatedReactions(state, id, finalReactionState);
};

const deleteRealtimeReaction = (state: Messages, action: MessageRealtimeReaction) => {
  const { id, reaction, userId } = action.payload;
  const message = state.fulfilled.byId[id];

  if (!message) {
    return state;
  }
  const updatedReactionState = removeFeaturedReaction(message, userId, reaction);

  return stateWithUpdatedReactions(state, id, updatedReactionState);
};

const updatePoll = (state: Messages, action: PollLoadFulfilledAction) => {
  const { payload } = action;
  const message = state.fulfilled.byId[payload.id];

  const messageWithUpdatedPollVotes = {
    ...message,
    content: {
      ...message.content,
      answers: action.payload.answers,
    },
  };

  return update(state, payload.id, messageWithUpdatedPollVotes);
};

export interface EntityWithContent extends Entity {
  readonly content: Message['content'];
}

export type OptimisticallyUpdatePoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  action: PollVotePendingAction
) => EntityMap<T>;
export const optimisticallyUpdatePoll: OptimisticallyUpdatePoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  { payload }: PollVotePendingAction
) => {
  const entity = state.fulfilled.byId[payload.id];
  if (!entity) {
    return state;
  }

  const content = entity.content;

  if (!isPollMessageContent(content)) {
    return state;
  }

  const entityWithUpdatedContent = {
    ...entity,
    content: {
      ...content,
      answers: content.answers.map((answer) => {
        if (answer.index === payload.index) {
          return {
            ...answer,
            isSelected: true,
            voteCount: answer.isSelected ? answer.voteCount : answer.voteCount + 1,
          };
        }

        return {
          ...answer,
          isSelected: false,
          voteCount: answer.isSelected ? answer.voteCount - 1 : answer.voteCount,
        };
      }),
    },
  };

  return update(state, payload.id, entityWithUpdatedContent);
};

const stateWithMessageAndRefsRemoved = (state: Messages, messageId: Message['id']) => {
  const messages = state.fulfilled.byId;

  const stateWithUpdateRepliedTo = Object.keys(messages).reduce((updatedState, currentId) => {
    const currentMessage = messages[currentId];

    if (currentMessage.repliedToId !== messageId) {
      return updatedState;
    }

    const { repliedToId, ...messageWithoutRepliedToId } = currentMessage;

    return moveToFulfilled(updatedState, { [currentId]: messageWithoutRepliedToId });
  }, state);

  return update(stateWithUpdateRepliedTo, messageId, { isDeleted: true });
};

const deleteMessage = (
  state: Messages,
  { messageId, deletionOutcome, parentMessageId }: DeleteMessageFulfilledAction['payload']
) => {
  if (deletionOutcome === MessageDeletionOutcome.MESSAGE_PLACEHOLDER_RETAINED) {
    return update(state, messageId, { isDeleted: true });
  }

  if (deletionOutcome === MessageDeletionOutcome.MESSAGE_REMOVED) {
    return stateWithMessageAndRefsRemoved(state, messageId);
  }

  if (deletionOutcome === MessageDeletionOutcome.THREAD_REMOVED) {
    return update(state, messageId, { isDeleted: true });
  }

  if (deletionOutcome === MessageDeletionOutcome.MESSAGE_AND_PARENT_REMOVED && parentMessageId !== undefined) {
    const stateWithParentSoftDeleted = update(state, parentMessageId, { isDeleted: true });

    return update(stateWithParentSoftDeleted, messageId, { isDeleted: true });
  }

  return state;
};

const markUnmarkAsQuestion = (
  state: Messages,
  action: ThreadMarkAsQuestionFulfilledAction | ThreadUnmarkAsQuestionFulfilledAction
) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { isQuestion, content } = message;
  const { title, body } = content as NormalMessageContent;
  let contentProps = {};

  if (isQuestion && !!title) {
    const newBody = prependTitleToRawContentState(title, body);

    contentProps = { title: undefined, body: newBody };
  }

  return update(state, messageId, {
    ...message,
    subtype: isQuestion ? MessageSubtype.UnmarkedAsQuestion : MessageSubtype.MarkedAsQuestion,
    isQuestion: !isQuestion,
    content: {
      ...content,
      ...contentProps,
    },
  });
};

const addUpvoteToMessage = (state: Messages, action: AddUpvoteRejected | AddUpvotePending) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { upvotesTotalCount } = message;

  return update(state, messageId, {
    ...message,
    viewerHasUpvoted: true,
    upvotesTotalCount: upvotesTotalCount + 1,
  });
};

const removeUpvoteToMessage = (state: Messages, action: RemoveUpvoteRejected | RemoveUpvotePending) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { upvotesTotalCount } = message;

  return update(state, messageId, {
    ...message,
    viewerHasUpvoted: false,
    upvotesTotalCount: upvotesTotalCount > 0 ? upvotesTotalCount - 1 : 0,
  });
};

const markHasFileDeletionFailed = (state: Messages, hasFailedFileDeletions: boolean, messageId?: Message['id']) => {
  if (messageId) {
    const message = state.fulfilled.byId[messageId];

    return update(state, messageId, {
      ...message,
      hasFailedFileDeletions,
    });
  }

  return state;
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Messages, MessageAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FILE.DELETE_REJECTED':
      return markHasFileDeletionFailed(state, true, action.payload.messageId);
    case 'MESSAGE.RESET_FAILED_FILE_DELETION':
      return markHasFileDeletionFailed(state, false, action.payload.messageId);
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.LOAD_NEWER_REPLIES_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.LOAD_OLDER_REPLIES_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.MARK_AS_QUESTION_FULFILLED':
      return markUnmarkAsQuestion(state, action);
    case 'THREAD.UNMARK_AS_QUESTION_FULFILLED':
      return markUnmarkAsQuestion(state, action);
    case 'THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NOTIFICATION.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'MESSAGE.LIKE_PENDING':
      return addUserLikeToMessage(state, action);
    case 'MESSAGE.UNLIKE_PENDING':
      return removeUserLikeFromMessage(state, action);
    case 'MESSAGE.REALTIME_REACTION_ADDED':
      return addRealtimeReaction(state, action);
    case 'MESSAGE.REALTIME_REACTION_DELETED':
      return deleteRealtimeReaction(state, action);
    case 'MESSAGE_UPVOTES.ADD_UPVOTE_PENDING':
      return addUpvoteToMessage(state, action);
    case 'MESSAGE_UPVOTES.REMOVE_UPVOTE_PENDING':
      return removeUpvoteToMessage(state, action);
    case 'POLL.VOTE_PENDING':
      return optimisticallyUpdatePoll(state, action);
    case 'POLL.LOAD_FULFILLED':
      return updatePoll(state, action);
    case 'MESSAGE.DELETE_FULFILLED':
      return deleteMessage(state, action.payload);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    default:
      return state;
  }
};

export default reducer;
